import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectTentativeETALocation } from '@rsos/reduxDataStore/src/features/airAmbulance';
import { useLazyGetTransportTentativeEtaQuery } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { TentativeETA } from '../../types';
import useAirAmbulanceEtlStatus from './useAirAmbulanceEtlStatus';

const ARRIVED_TYPES = new Set([
  'arrivedToScene',
  'arrivedToDropoff',
  'complete',
]);

function formatETADisplayText(timestamp: string, label: string): string {
  const date = new Date(timestamp);
  const now = new Date();

  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const diffMinutes = Math.round((date.getTime() - now.getTime()) / 60000);

  return `${label}: ${time} (${diffMinutes} minutes)`;
}

/**
 * Return the latest ETA information that is available.
 *
 * If we already have a request selected and the ETA is available from the
 * status information, we ues the ETA provided by GMR in the status update.
 * Otherwise we fetch the tentative ETA from GMR using the etl-integrations
 * service. The "tentative ETA" is an estimate GMR provides when a transport
 * hasn't been dispatched yet.
 */
function useAirAmbulanceEtlEta(): TentativeETA | null {
  const tentativeETALocation = useSelector(selectTentativeETALocation);
  const { latestProvider, latestEta, latestStatus } =
    useAirAmbulanceEtlStatus();
  const [trigger, { data }] = useLazyGetTransportTentativeEtaQuery();
  const isArrived = latestStatus?.type
    ? ARRIVED_TYPES.has(latestStatus.type)
    : false;

  useEffect(() => {
    if (latestEta || !tentativeETALocation) return;

    trigger(tentativeETALocation);
  }, [tentativeETALocation, latestEta, trigger]);

  if (latestEta) {
    // Case where we get the ETA from status updates of an already dispatched
    // transport.  In this case we need to change the label to reflect weather
    // the transport arrived already.
    const label = isArrived ? 'Arrived at' : 'ETA';
    return {
      display: formatETADisplayText(latestEta, label),
      providers: latestProvider?.name || null,
    };
  }

  if (data) {
    // Case where we don't have a status update from a dispatched transport,
    // and we had to query GMR to get a tentative ETA (i.e. estimate).
    // Label should always reflect that it is tentative.
    return {
      display: formatETADisplayText(data.eta, 'Tentative ETA'),
      providers: data.providers,
    };
  }

  return null;
}

export default useAirAmbulanceEtlEta;
